// Ownership helpers for "Do This Next".
// Kept import-free so Deno CI can unit-test them in isolation.

export interface DeliverableOwnerFields {
  currentOwner?: string;
  workingTeam?: string[];
}

function normalizeName(name: unknown): string | undefined {
  if (typeof name !== 'string') return undefined;
  const trimmed = name.trim();
  return trimmed || undefined;
}

/**
 * Who is on the hook. Prefers the explicit handoff owner; otherwise the
 * first working-team member (the primary assignee).
 */
export function getDeliverableOwner(d: DeliverableOwnerFields): string | undefined {
  const owner = normalizeName(d.currentOwner);
  if (owner) return owner;
  if (Array.isArray(d.workingTeam)) {
    for (const name of d.workingTeam) {
      const primary = normalizeName(name);
      if (primary) return primary;
    }
  }
  return undefined;
}

/**
 * True when this user is the owner *or* is on the working team. Used to
 * scope "Do This Next" to work associated with the signed-in person.
 */
export function isOwnedByUser(
  d: DeliverableOwnerFields,
  userName: string | null | undefined,
): boolean {
  const user = normalizeName(userName);
  if (!user) return false;
  if (normalizeName(d.currentOwner) === user) return true;
  return Array.isArray(d.workingTeam) && d.workingTeam.some(name => normalizeName(name) === user);
}

/** Stamp currentOwner from the working team when a create/update omitted it. */
export function withDeliverableOwner<T extends Partial<DeliverableOwnerFields>>(data: T): T {
  const existing = normalizeName(data.currentOwner);
  if (existing) {
    return data.currentOwner === existing ? data : { ...data, currentOwner: existing };
  }
  const owner = Array.isArray(data.workingTeam)
    ? data.workingTeam.map(normalizeName).find((name): name is string => !!name)
    : undefined;
  if (!owner) return data;
  return { ...data, currentOwner: owner };
}
